import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApprovePublishDialogComponent } from '@components/approve-publish-dialog/approve-publish-dialog.component';
import { PublishVesselDialogComponent } from '@components/publish-vessel-dialog/publish-vessel-dialog.component';
import { RejectPublishDialogComponent } from '@components/reject-publish-dialog/reject-publish-dialog.component';
import { UnlockVesselDialogComponent } from '@components/unlock-vessel-dialog/unlock-vessel-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@service/auth.service';
import { PublishRequestSocketService } from '@service/publish-request-socket.service';
import { PublishService } from '@service/publish.service';
import { PullProjectService } from '@service/pull-project.service';
import { VesselService } from '@service/vessel.service';
import { ApprovalRequest } from 'icreate/common/models/approval-request';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { Subject, lastValueFrom, take, takeUntil } from 'rxjs';
@Component({
    selector: 'app-request-approval-message',
    templateUrl: './request-approval-message.component.html',
    styleUrls: ['./request-approval-message.component.css'],
    standalone: false,
})
export class RequestApprovalMessageComponent implements OnInit, OnDestroy, OnChanges {
    @Input() versionToApprove: string;
    @Input() vessel: Vessel;
    @Input() shipyardiApi: string;
    @Output() onProjectStatusChanged = new EventEmitter<ProjectStatus>();

    showMessage: boolean = false;
    showAfterApprovedMessage: boolean = false;
    isLoading: boolean = false;
    publishReason: ApprovalRequest;
    reasonString: string;
    reasonTranslationPrefix: string = 'vessel.page.message.pending.reason';
    messageType: ApprovalRequestMessageType;
    currentUser: { name: string; oid: string; email: string };

    private destroySubscriptions$: Subject<void> = new Subject();

    constructor(
        private authService: AuthService,
        private pullService: PullProjectService,
        private translateService: TranslateService,
        private vesselService: VesselService,
        private publishService: PublishService,
        private publishRequestSocketService: PublishRequestSocketService,
        private router: Router,
        private dialog: MatDialog
    ) {}

    async ngOnInit() {
        this.startSubscriptions();
        this.currentUser = this.authService.getCurrentUser();
        this.getMessageDisplayed();
        if (this.showMessage) {
            await this.loadPublishReason();
            this.prepareReasonsString();
        }
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.vessel?.previousValue || changes.vessel !== changes.vessel?.previousValue) {
            if (this.vessel.projectStatus === ProjectStatus.Editing) {
                this.showAfterApprovedMessage = false;
            }
            this.getMessageDisplayed();
            if (this.showMessage) {
                await this.loadPublishReason();
                this.prepareReasonsString();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    async loadPublishReason() {
        this.publishReason = await this.pullService.getPublishReason(
            this.vessel.iAPIVesselId,
            this.shipyardiApi,
            this.vessel.projectVersion
        );
    }

    showPendingMessage(): boolean {
        return (
            this.messageType === ApprovalRequestMessageType.REQUEST_APPROVAL_MESSAGE &&
            this.currentUser?.oid === this.publishReason?.approverOid
        );
    }

    showApprovedMessage(): boolean {
        return (
            this.messageType === ApprovalRequestMessageType.APPROVED_MESSAGE &&
            this.currentUser?.oid === this.vessel?.ownerOid
        );
    }

    showApprovedMessageApprover(): boolean {
        return (
            this.messageType === ApprovalRequestMessageType.APPROVED_MESSAGE &&
            this.currentUser?.oid === this.publishReason?.approverOid
        );
    }

    showRejectedMessage(): boolean {
        return (
            this.messageType === ApprovalRequestMessageType.REJECTED_MESSAGE &&
            this.currentUser?.oid === this.vessel?.ownerOid
        );
    }

    showRejectedMessageApprover(): boolean {
        return (
            this.messageType === ApprovalRequestMessageType.REJECTED_MESSAGE &&
            this.currentUser?.oid === this.publishReason?.approverOid
        );
    }

    hideMessage() {
        this.showAfterApprovedMessage = false;
    }

    prepareReasonsString() {
        if (!this.publishReason) {
            return;
        }
        const reasonObject = this.publishReason?.reason;

        const reasonObjectKeys = Object.keys(reasonObject);
        const reasonsArray = [];
        reasonObjectKeys.map(reason => {
            if (reason !== 'comment' && reasonObject[reason]) {
                if (reason == 'other') {
                    if (reasonObject[reason]) {
                        reasonsArray.push(this.publishReason.reason.other);
                    }
                } else {
                    let transalation = this.translateService.instant(this.reasonTranslationPrefix.concat('.', reason));
                    reasonsArray.push(transalation);
                }
            }
        });
        this.reasonString = reasonsArray.join(', ');
    }

    async openApproveDialog() {
        const dialogRef = this.dialog.open(ApprovePublishDialogComponent, {
            width: '474px',
            data: { version: this.publishReason.version, userName: this.publishReason.ownerName },
        });
        const approvePublish = await lastValueFrom(dialogRef.afterClosed());
        if (approvePublish) {
            try {
                await this.publishService.approvePublish(
                    this.publishReason.publishRequestId,
                    this.publishReason.vesselId,
                    this.publishReason.approverOid
                );
                this.onProjectStatusChanged.emit(ProjectStatus.Approved);
                this.showMessage = false;
            } catch (err) {
                console.error(err);
            }
        }
    }

    async openRejectDialog() {
        const dialogRef = this.dialog.open(RejectPublishDialogComponent, { width: '474px' });
        const rejectPublish = await lastValueFrom(dialogRef.afterClosed());
        if (rejectPublish) {
            try {
                const rejectReason = rejectPublish;
                await this.publishService.rejectPublish(
                    this.publishReason.publishRequestId,
                    rejectReason,
                    this.publishReason.vesselId,
                    this.publishReason.approverOid
                );
                this.onProjectStatusChanged.emit(ProjectStatus.Rejected);
                this.showMessage = false;
            } catch (err) {
                console.error(err);
            }
        }
    }

    async hideRejectedMessage() {
        try {
            await this.vesselService.unlockVessel(
                this.vessel.id,
                this.publishReason.shipyardiApi,
                this.vessel.iAPIVesselId,
                this.publishReason.version
            );
            this.onProjectStatusChanged.emit(ProjectStatus.Editing);
            this.showMessage = false;
        } catch (err) {
            console.error(err);
        }
    }

    async unlockVessel() {
        const dialogRef = this.dialog.open(UnlockVesselDialogComponent, { width: '474px' });
        const shouldUnlock = await lastValueFrom(dialogRef.afterClosed());
        if (shouldUnlock) {
            this.isLoading = true;
            try {
                await this.vesselService.unlockVessel(
                    this.vessel.id,
                    this.shipyardiApi,
                    this.vessel.iAPIVesselId,
                    this.vessel.projectVersion
                );
                this.onProjectStatusChanged.emit(ProjectStatus.Editing);
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        }
    }

    async publishProject() {
        const dialogRef = this.dialog.open(PublishVesselDialogComponent, {
            width: '474px',
            data: { projectVersion: this.vessel.projectVersion },
        });
        const shouldPublish = await lastValueFrom(dialogRef.afterClosed());
        if (shouldPublish) {
            this.isLoading = true;
            try {
                await this.publishService.publish(
                    this.vessel.iAPIVesselId,
                    this.shipyardiApi,
                    this.vessel.projectVersion,
                    this.vessel.id
                );
                this.showAfterApprovedMessage = true;
                this.onProjectStatusChanged.emit(ProjectStatus.undefined);
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    private getMessageDisplayed() {
        this.showMessage = false;
        this.messageType = null;
        this.publishReason = null;
        if (this.versionToApprove && this.vessel.projectStatus === ProjectStatus.PendingApproval) {
            this.showMessage = true;
            this.messageType = ApprovalRequestMessageType.REQUEST_APPROVAL_MESSAGE;
        } else if (this.vessel.projectStatus === ProjectStatus.Approved) {
            this.showMessage = true;
            this.messageType = ApprovalRequestMessageType.APPROVED_MESSAGE;
        } else if (this.vessel.projectStatus === ProjectStatus.Rejected) {
            this.showMessage = true;
            this.messageType = ApprovalRequestMessageType.REJECTED_MESSAGE;
        }
    }

    private startSubscriptions() {
        this.publishRequestSocketService
            .requestAccepted$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    this.vessel = updatedVessel;
                    this.getMessageDisplayed();
                    if (this.showMessage) {
                        await this.loadPublishReason();
                        this.prepareReasonsString();
                    }
                }
            });

        this.publishRequestSocketService
            .requestRejected$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    this.vessel = updatedVessel;
                    this.getMessageDisplayed();
                    if (this.showMessage) {
                        await this.loadPublishReason();
                        this.prepareReasonsString();
                    }
                }
            });
    }
}

export enum ApprovalRequestMessageType {
    APPROVED_MESSAGE = 'approved-owner',
    REJECTED_MESSAGE = 'rejected',
    REQUEST_APPROVAL_MESSAGE = 'request',
}
